import axios from "axios";
import { API_URL } from "../constants";

/**
 * What the cabinet shows about the person themselves: the assets they hold
 * (`GET /:companyId/cabinet/my-assets`) and their employment (`GET /:companyId/cabinet/my-work`).
 * Both answer only for the signed-in person in this company.
 */

/**
 * How the newest assign act of an asset stands for the person. The first rule that applies:
 * no act (`none`), fully signed (`signed`), the person's own signature pending (`to_sign`),
 * otherwise somebody else's (`waiting_others`).
 */
export type AssetActStatus = "none" | "signed" | "to_sign" | "waiting_others";

export interface MyAsset {
  id: string;
  name: string;
  type: string | null;
  brand: string | null;
  model: string | null;
  serial: string | null;
  /** YYYY-MM-DD */
  heldSince: string | null;
  act: {
    status: AssetActStatus;
    fileId: string | null;
    /** The document page, where the act is opened and signed; null without an act. */
    link: string | null;
  };
}

/** The assets assigned to the person now, the most recently received first. */
export async function fetchMyAssets(companyId: string): Promise<MyAsset[]> {
  const res = await axios.get(`${API_URL}/${companyId}/cabinet/my-assets`);
  return res.data.items;
}

export interface MyWorkTerms {
  contractType: string | null;
  contractVariant: string | null;
  originalContractNumber: string | null;
  /** YYYY-MM-DD */
  contractEndDate: string | null;
  hoursPerWeek: number | null;
  probationMonths: number | null;
  /** YYYY-MM-DD; only while the probation is running or ended within the last 30 days. */
  probationEndDate: string | null;
  salary: number | null;
  plannedSalaryChange: { date: string; amount: number } | null;
}

export interface MyWork {
  positionsText: string | null;
  departments: { id: string; name: string }[];
  manager: { id: string; name: string } | null;
  /** YYYY-MM-DD */
  hiringDate: string | null;
  /** null when the person has no onboarding or employment record in this company. */
  terms: MyWorkTerms | null;
  contract: { fileId: string; title: string; isSigned: boolean } | null;
  /** Signed amendments, newest effective date first. */
  amendments: {
    id: string;
    /** YYYY-MM-DD */
    effectiveDate: string;
    isEmploymentTermsChange: boolean;
    fileId: string | null;
  }[];
}

export async function fetchMyWork(companyId: string): Promise<MyWork> {
  const res = await axios.get(`${API_URL}/${companyId}/cabinet/my-work`);
  return res.data;
}
